import React, { useCallback, useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';

import SheetsList from '../SheetsList/SheetsList';
import SheetForm from '../SheetForm/SheetForm';
import SortingAndFilteringPanel from '../SortingAndFilteringPanel';
import WorklogDetails from 'components/WorklogDetails';
import { PAGE_CONTEXTS } from 'components/WorklogDetails/constants';
import ResourcesForm from 'components/ResourcesForm';
import CommentsForm from 'components/CommentsForm';
import MaterialForm from 'components/MaterialForm';
import FiltersBar from 'components/FiltersBar';
import LoadingSpinner from 'components/LoadingSpinner';
import TimeForm from 'components/WorklogDetails/components/TimeBlock/components/TimeForm/TimeForm';
import WeatherForm from 'components/WorklogDetails/components/WeatherBlock/components/WeatherForm/WeatherForm';
import NotesForm from 'components/WorklogDetails/components/NotesBlock/components/NotesForm/NotesForm';
import GeneralInfoForm from 'components/WorklogDetails/components/GeneralInfoBlock/components/GeneralInfoForm/GeneralInfoForm';
import { useDispatch, useSelector } from 'react-redux';
import { unstable_batchedUpdates } from 'react-dom';
import { startOfDay, endOfDay, sub } from 'date-fns';
import {
  getSubmittedWorklogs,
  worklogsActions,
  selectSubmittedWorklogs,
  selectSheetIds,
  selectSubmittedHasMore,
  selectForceUpdate,
} from 'store/worklogs';

const styles = {
  backdropOpacity: {
    opacity: '0 !important',
  },
};

const SubmittedWorklogs = (props) => {
  const dispatch = useDispatch();
  const { classes, openSnackbar, params } = props;

  const sheets = useSelector(selectSubmittedWorklogs);
  const sheetIds = useSelector(selectSheetIds);
  const hasMore = useSelector(selectSubmittedHasMore);
  const forceUpdate = useSelector(selectForceUpdate);

  const [drawer, setDrawer] = useState({
    type: 'filters',
    isOpen: false,
    sheet: {},
  });
  const [additionalDrawer, setAdditionalDrawer] = useState({
    type: 'resources',
    subType: 'people',
    isOpen: false,
    material: {},
  });
  const [filter, setFilter] = useState({
    projectId: 'All',
    contractorId: 'All',
    dates: [sub(startOfDay(new Date()), { weeks: 2 }), endOfDay(new Date())],
    status: 'All',
    shift: 'All',
    forceAccount: 'All',
    rated: 'All',
  });
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const latestSearchId = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [dates, setDates] = useState([sub(startOfDay(new Date()), { weeks: 2 }), endOfDay(new Date())]);

  useEffect(() => {
    if (!forceUpdate) return
    const limit = page === 0 ?  40 : (page * 40)

    dispatch(getSubmittedWorklogs({searchValue, filter, page,  skip: '0', limit, isClear: true}))
  }, [forceUpdate]);

  useEffect(() => {
    return ()=>{
      dispatch(worklogsActions.resetWorklogs())
    }
  }, []);

  useEffect(() => {
    updateVisibleSheets();
  }, [searchValue, filter]);

  const updateSearchValue = useCallback((value) => {
    unstable_batchedUpdates(() => {
      setPage(0);

      if (!searchValue && value) {
        setFilter({ ...filter, dates: [null, null] })
      } else if (!value) {
        setFilter({ ...filter, dates });
      }

      setSearchValue(value);
    });
  }, [filter, dates]);

  const updateFilter = (filter) => {
    setPage(0);
    setFilter(filter);

    if (filter?.dates?.[0] && filter?.dates?.[1]) {
      setDates(filter.dates);
    }

    setDrawer((prev) => ({
      ...prev,
      type: 'details',
      isOpen: false,
    }));
  };

  const updateVisibleSheets = async ({ append } = {}) => {
    const setLoading = append ? setIsLoadingMore : setIsSearching;
    setLoading(true);

    const p = dispatch(getSubmittedWorklogs({ searchValue, filter, page }));
    if (!append) latestSearchId.current = p.requestId;
    await p;
    setPage((prev) => prev + 1);
    // A superseded fresh request must not clear the spinner the latest one still needs.
    if (append || p.requestId === latestSearchId.current) setLoading(false);
  };

  const openDrawer =
    (type, sheet = {}) =>
    () => {
      setDrawer({ type, isOpen: true, sheet });
    };

  const closeDrawer = () => {
    setDrawer((prev) => ({ ...prev, type: 'details', isOpen: false }));
  };

  const openAdditionalDrawer =
    (type, subType, material = {}) =>
    () => {
      setAdditionalDrawer({ type, subType, isOpen: true, material });
    };

  const closeAdditionalDrawer = () => {
    setAdditionalDrawer((prev) => ({ ...prev, isOpen: false }));
  };

  const getFullRefreshedList = (sheet) => {
    dispatch(worklogsActions.setRefreshSubmittedSheet(sheet))
  };

  const sheetId = params.sheetId;

  return (
    <>
      <FiltersBar openDrawer={openDrawer} updateSearchValue={updateSearchValue} isDynamicSearch={true} onSearchStart={() => setIsSearching(true)}/>

        {isSearching ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '100px' }}>
            <LoadingSpinner height={40} margin={32} />
          </div>
        ) : (
          <SheetsList
            openSnackbar={openSnackbar}
            openDrawer={openDrawer}
            sheets={sheets}
            updateVisibleSheets={() => updateVisibleSheets({ append: true })}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            page={page}
            submittedType
            filter={filter}
          />
        )}

      {sheetId && !isSearching &&  (
        <WorklogDetails
          openSnackbar={props.openSnackbar}
          updateVisibleSheets={updateVisibleSheets}
          getFullRefreshedList={getFullRefreshedList}
          openDrawer={openDrawer}
          openAdditionalDrawer={openAdditionalDrawer}
          updateDrawerFromRouter={closeDrawer}
          sheetIds={sheetIds}
          hasMore={hasMore}
          // pageCount={pageCount}
          pageContext={PAGE_CONTEXTS.worklogs}
        />
      )}

      <Drawer
        anchor="right"
        open={drawer.isOpen}
        ModalProps={{
          BackdropProps: {
            classes: {
              root: sheetId ? classes.backdropOpacity : null,
            },
          },
        }}
      >
        {drawer.type === 'update' && (
          <SheetForm
            closeDrawer={closeDrawer}
            type={drawer.type}
            sheet={drawer.sheet}
            openSnackbar={props.openSnackbar}
          />
        )}
        {drawer.type === 'filters' && (
          <SortingAndFilteringPanel
            closeDrawer={closeDrawer}
            filter={filter}
            updateFilter={updateFilter}
            isLoading={isSearching}
            submissions={true}
          />
        )}
      </Drawer>

      <Drawer anchor="left" open={drawer.type === 'details' && additionalDrawer.isOpen}>
        {additionalDrawer.type === 'resources' && (
          <ResourcesForm closeDrawer={closeAdditionalDrawer} type={additionalDrawer.subType} openSnackbar={openSnackbar}/>
        )}
        {additionalDrawer.type === 'comments' && (
          <CommentsForm closeDrawer={closeAdditionalDrawer} openSnackbar={openSnackbar} />
        )}
        {additionalDrawer.type === 'material' && (
          <MaterialForm
            closeDrawer={closeAdditionalDrawer}
            openSnackbar={openSnackbar}
            type={additionalDrawer.subType}
            material={additionalDrawer.material}
          />
        )}

        {additionalDrawer.type === 'time' && (
          <TimeForm closeDrawer={closeAdditionalDrawer} openSnackbar={openSnackbar} />
        )}
        {additionalDrawer.type === 'weather' && <WeatherForm closeDrawer={closeAdditionalDrawer} />}
        {additionalDrawer.type === 'notes' && (
          <NotesForm closeDrawer={closeAdditionalDrawer} openSnackbar={openSnackbar} />
        )}
        {additionalDrawer.type === 'general' && (
          <GeneralInfoForm closeDrawer={closeAdditionalDrawer} />
        )}
      </Drawer>
    </>
  );
};

SubmittedWorklogs.propTypes = {
  classes: propTypes.object.isRequired,
  openSnackbar: propTypes.func.isRequired,
};

export default withStyles(styles)(SubmittedWorklogs);
